// pages/trucker/TruckerRegistration.js

import React, { useState, useEffect, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import SignatureCanvas from "react-signature-canvas"
import apiPublic from "../../services/apiPublic"
import { useTranslation } from "react-i18next"
import "../../assets/css/truckerregistration.css"
import kreuzImg from "../../assets/images/kreuz.jpg"
import signsImg from "../../assets/images/signs.jpg"

export default function TruckerRegistration() {
  console.log("[FRONTEND] Rendering TruckerRegistration component")
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [barcodeId, setBarcodeId] = useState("")
  const [name, setName] = useState("")
  const [surname, setSurname] = useState("")
  const [phonePrefix, setPhonePrefix] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [preferredLanguage, setPreferredLanguage] = useState("")
  const [languages, setLanguages] = useState([])
  const [signatureData, setSignatureData] = useState("")
  const [consentReadEverything, setConsentReadEverything] = useState(false)
  const [consentStoreData, setConsentStoreData] = useState(false)
  const [loadingNumber, setLoadingNumber] = useState("")
  const [freightCompanyName, setFreightCompanyName] = useState("")
  const [freightCompanyCity, setFreightCompanyCity] = useState("")
  const [freightCompanyCountry, setFreightCompanyCountry] = useState("")
  const [licensePlateTruck, setLicensePlateTruck] = useState("")
  const [licensePlateTrailer, setLicensePlateTrailer] = useState("")
  const [hasFoodAdvertising, setHasFoodAdvertising] = useState(false)
  const [isFoodLoad, setIsFoodLoad] = useState(false)
  const [status, setStatus] = useState("")
  const [registered, setRegistered] = useState(false)
  const [missingFields, setMissingFields] = useState([])
  const sigCanvasRef = useRef(null)
  const europeanPrefixes = [
    { code: "+49", label: "Germany (+49)" },
    { code: "+48", label: "Poland (+48)" },
    { code: "+420", label: "Czech Republic (+420)" },
    { code: "+421", label: "Slovakia (+421)" },
    { code: "+36", label: "Hungary (+36)" },
    { code: "+359", label: "Bulgaria (+359)" },
    { code: "+40", label: "Romania (+40)" },
    { code: "+380", label: "Ukraine (+380)" },
    { code: "+44", label: "UK (+44)" },
    { code: "+43", label: "Austria (+43)" },
    { code: "+39", label: "Italy (+39)" },
    { code: "+34", label: "Spain (+34)" },
    { code: "+33", label: "France (+33)" },
    { code: "+46", label: "Sweden (+46)" },
    { code: "+45", label: "Denmark (+45)" },
    { code: "+41", label: "Switzerland (+41)" },
    { code: "+31", label: "Netherlands (+31)" },
    { code: "+32", label: "Belgium (+32)" },
    { code: "+354", label: "Iceland (+354)" },
    { code: "+7", label: "Russia (+7)" }
  ]

  useEffect(() => {
    console.log("[FRONTEND] useEffect triggered, loading languages and barcodeId")
    const bId = searchParams.get("barcodeId") || ""
    setBarcodeId(bId)
    loadLanguages()
  }, [searchParams])

  const loadLanguages = async () => {
    console.log("[FRONTEND] loadLanguages start")
    try {
      const r = await apiPublic.get("/language")
      setLanguages(r.data.languages)
      console.log("[FRONTEND] languages loaded:", r.data.languages)
    } catch {
      console.log("[FRONTEND] error loading languages")
      setStatus(t("trucker_registration.errors.load_languages"))
    }
  }

  const clearSignature = () => {
    console.log("[FRONTEND] clearSignature clicked")
    sigCanvasRef.current.clear()
    setSignatureData("")
  }

  const saveSignature = () => {
    console.log("[FRONTEND] saveSignature triggered")
    if (!sigCanvasRef.current.isEmpty()) {
      const dataURL = sigCanvasRef.current.getCanvas().toDataURL("image/png")
      setSignatureData(dataURL)
      console.log("[FRONTEND] signature captured, length:", dataURL.length)
    }
  }

  const handleRegister = async () => {
    console.log("[FRONTEND] handleRegister triggered")
    const neededFields = []
    const cleanedNumber = phoneNumber.replace(/^0+/, "")
    const finalPhone = phonePrefix + cleanedNumber
    if (!barcodeId) neededFields.push("barcodeId")
    if (!name) neededFields.push("name")
    if (!surname) neededFields.push("surname")
    if (!phonePrefix || !cleanedNumber) neededFields.push("phoneNumber")
    if (!signatureData) neededFields.push("signature")
    if (!consentReadEverything) neededFields.push("consentReadEverything")
    if (!consentStoreData) neededFields.push("consentStoreData")
    if (!loadingNumber) neededFields.push("loadingNumber")
    if (!freightCompanyName) neededFields.push("freightCompanyName")
    if (!freightCompanyCity) neededFields.push("freightCompanyCity")
    if (!freightCompanyCountry) neededFields.push("freightCompanyCountry")
    if (!licensePlateTruck) neededFields.push("licensePlateTruck")
    if (!licensePlateTrailer) neededFields.push("licensePlateTrailer")
    setMissingFields(neededFields)
    if (neededFields.length) {
      console.log("[FRONTEND] missing fields:", neededFields)
      setStatus(t("trucker_registration.errors.missing_fields"))
      return
    }
    if (!/^\+[1-9]\d{7,14}$/.test(finalPhone)) {
      console.log("[FRONTEND] invalid phone format:", finalPhone)
      setStatus(t("trucker_registration.errors.invalid_phone_format"))
      return
    }
    try {
      console.log("[FRONTEND] sending register request, preferredLanguage:", preferredLanguage)
      const reg = await apiPublic.post("/trucker/register", {
        barcodeId,
        name,
        surname,
        phone: finalPhone,
        preferredLanguage,
        signature: signatureData,
        consentReadEverything,
        consentStoreData,
        loadingNumber,
        freightCompanyName,
        freightCompanyCity,
        freightCompanyCountry,
        licensePlateTruck,
        licensePlateTrailer,
        hasFoodAdvertising,
        isFoodLoad
      })
      console.log("[FRONTEND] register success:", reg.data)
      setStatus(reg.data.message)
      setRegistered(true)
    } catch (err) {
      console.log("[FRONTEND] register error:", err.response?.data)
      setStatus(err.response?.data?.error || "Error")
    }
  }

  if (!registered) {
    console.log("[FRONTEND] not registered, showing form")
    return (
      <div className="container page-container">
        <h2 className="mb-1">{t("trucker_registration.title")}</h2>
        <p className="mb-1">{t("trucker_registration.intro1")}</p>
        <ol className="mb-1" style={{ paddingLeft: "20px" }}>
          <li>{t("trucker_registration.rules.item1")}</li>
          <li>{t("trucker_registration.rules.item2")}</li>
          <li>{t("trucker_registration.rules.item3")}</li>
          <li>{t("trucker_registration.rules.item4")}</li>
          <li>{t("trucker_registration.rules.item5")}</li>
          <li>{t("trucker_registration.rules.item6")}</li>
        </ol>
        <div className="image-row">
          <img src={kreuzImg} alt="Kreuz" className="small-image" />
          <div className="text-box">{t("trucker_registration.image_row_text")}</div>
        </div>
        <img src={signsImg} alt="Signs" className="full-width-image" />
        <p className="mb-1">{t("trucker_registration.all_fields_mandatory")}</p>
        <div className={`form-group ${missingFields.includes("name") ? "missing-field" : ""}`}>
          <label>{t("trucker_registration.form_fields.name")}</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className={`form-group ${missingFields.includes("surname") ? "missing-field" : ""}`}>
          <label>{t("trucker_registration.form_fields.surname")}</label>
          <input type="text" value={surname} onChange={(e) => setSurname(e.target.value)} />
        </div>
        <div className={`phone-group ${missingFields.includes("phoneNumber") ? "missing-field" : ""}`}>
          <label>{t("trucker_registration.form_fields.phoneNumber")}</label>
          <div className="phone-container">
            <select className="phone-prefix" value={phonePrefix} onChange={(e) => setPhonePrefix(e.target.value)}>
              <option value="">{t("trucker_registration.form_fields.select_prefix")}</option>
              {europeanPrefixes.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.label}
                </option>
              ))}
            </select>
            <input
              className="phone-input"
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          </div>
        </div>
        <div className="form-group">
          <label>{t("trucker_registration.form_fields.preferredLanguage")}</label>
          <select value={preferredLanguage} onChange={(e) => setPreferredLanguage(e.target.value)}>
            <option value="">{t("trucker_registration.form_fields.choose_language")}</option>
            {languages.map((l) => (
              <option key={l._id} value={l.code}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        <div className={`form-group ${missingFields.includes("loadingNumber") ? "missing-field" : ""}`}>
          <label>{t("trucker_registration.form_fields.loadingNumber")}</label>
          <input type="text" value={loadingNumber} onChange={(e) => setLoadingNumber(e.target.value)} />
        </div>
        <div className={`form-group ${missingFields.includes("freightCompanyName") ? "missing-field" : ""}`}>
          <label>{t("trucker_registration.form_fields.freightCompanyName")}</label>
          <input type="text" value={freightCompanyName} onChange={(e) => setFreightCompanyName(e.target.value)} />
        </div>
        <div className={`form-group ${missingFields.includes("freightCompanyCity") ? "missing-field" : ""}`}>
          <label>{t("trucker_registration.form_fields.freightCompanyCity")}</label>
          <input type="text" value={freightCompanyCity} onChange={(e) => setFreightCompanyCity(e.target.value)} />
        </div>
        <div className={`form-group ${missingFields.includes("freightCompanyCountry") ? "missing-field" : ""}`}>
          <label>{t("trucker_registration.form_fields.freightCompanyCountry")}</label>
          <input type="text" value={freightCompanyCountry} onChange={(e) => setFreightCompanyCountry(e.target.value)} />
        </div>
        <div className={`form-group ${missingFields.includes("licensePlateTruck") ? "missing-field" : ""}`}>
          <label>{t("trucker_registration.form_fields.licensePlateTruck")}</label>
          <input type="text" value={licensePlateTruck} onChange={(e) => setLicensePlateTruck(e.target.value)} />
        </div>
        <div className={`form-group ${missingFields.includes("licensePlateTrailer") ? "missing-field" : ""}`}>
          <label>{t("trucker_registration.form_fields.licensePlateTrailer")}</label>
          <input type="text" value={licensePlateTrailer} onChange={(e) => setLicensePlateTrailer(e.target.value)} />
        </div>
        <div className="mb-1">
          <label>
            <input
              type="checkbox"
              checked={hasFoodAdvertising}
              onChange={(e) => setHasFoodAdvertising(e.target.checked)}
            />{" "}
            {t("trucker_registration.form_fields.hasFoodAdvertising")}
          </label>
        </div>
        <div className="mb-1">
          <label>
            <input
              type="checkbox"
              checked={isFoodLoad}
              onChange={(e) => setIsFoodLoad(e.target.checked)}
            />{" "}
            {t("trucker_registration.form_fields.isFoodLoad")}
          </label>
        </div>
        <h4 className="mt-2">{t("trucker_registration.data_consents")}</h4>
        <div className={`mb-1 ${missingFields.includes("consentStoreData") ? "missing-field" : ""}`}>
          <label>
            <input
              type="checkbox"
              checked={consentStoreData}
              onChange={(e) => setConsentStoreData(e.target.checked)}
            />{" "}
            {t("trucker_registration.form_fields.consentStoreData")}
          </label>
        </div>
        <div className={`mb-1 ${missingFields.includes("consentReadEverything") ? "missing-field" : ""}`}>
          <label>
            <input
              type="checkbox"
              checked={consentReadEverything}
              onChange={(e) => setConsentReadEverything(e.target.checked)}
            />{" "}
            {t("trucker_registration.form_fields.consentReadEverything")}
          </label>
        </div>
        <div className={`signature-container ${missingFields.includes("signature") ? "missing-field" : ""}`}>
          <label>{t("trucker_registration.form_fields.signature")}</label>
          <SignatureCanvas
            ref={sigCanvasRef}
            penColor="black"
            canvasProps={{ className: "sigCanvas" }}
            onEnd={saveSignature}
          />
        </div>
        <div className="mt-1">
          <button className="btn" onClick={clearSignature} style={{ marginRight: 10 }}>
            {t("trucker_registration.buttons.clear_signature")}
          </button>
          <button className="btn" onClick={handleRegister}>
            {t("trucker_registration.buttons.register")}
          </button>
        </div>
        <p className="mt-1">{status}</p>
      </div>
    )
  }
  console.log("[FRONTEND] user is registered, showing success screen")
  return (
    <div className="container page-container">
      <h2>{t("trucker_registration.registration_complete_title")}</h2>
      <p>{status}</p>
      <p>{t("trucker_registration.registration_complete_message")}</p>
    </div>
  )
}